/**
 * Tab Indicator — reusable sliding highlight for any group of tabs/buttons.
 *
 * Container attributes:
 *   data-tab-indicator                     → required, marks the group
 *   data-tab-indicator-active-class="x"    → class that marks the active tab (default: "is-active";
 *                                            Webflow's "w--current" and aria-selected="true" are always detected)
 *   data-tab-indicator-hover="false"       → disable follow-on-hover (default: true)
 *   data-tab-indicator-click="true"        → script manages the active class on click (use for non-Webflow tabs)
 *   data-tab-indicator-clip="true"         → adds the white text "wipe" reveal inside each tab
 *   data-tab-indicator-duration="0.45"     → seconds
 *   data-tab-indicator-ease="ease"         → any CSS easing
 *
 * Child attributes:
 *   data-tab-indicator-item                → each tab (falls back to direct children if none are marked)
 *   data-tab-indicator-label               → optional, the element cloned for the clip text (default: first child)
 *   data-tab-indicator-bar                 → optional, your own indicator element (otherwise one is created)
 *
 * Event:
 *   container fires "tabindicator:change" with detail { index, tab } when the active tab changes.
 *
 * API:
 *   initTabIndicator(el, options?) → { update, setActive(index), destroy, activeIndex }
 *   initTabIndicators(root?)       → inits every [data-tab-indicator] inside root
 */
(function () {
  const DEFAULTS = {
    activeClass: 'is-active',
    hover: true,
    click: false,
    clip: false,
    duration: null,
    ease: null,
  };

  function bool(val, fallback) {
    if (val === undefined || val === null || val === '') return fallback;
    return String(val) !== 'false';
  }

  function initTabIndicator(container, options = {}) {
    if (!container) return null;
    if (container._tabIndicator) return container._tabIndicator;

    const ds = container.dataset;
    const cfg = {
      activeClass: ds.tabIndicatorActiveClass || options.activeClass || DEFAULTS.activeClass,
      hover: bool(ds.tabIndicatorHover, options.hover ?? DEFAULTS.hover),
      click: bool(ds.tabIndicatorClick, options.click ?? DEFAULTS.click),
      clip: bool(ds.tabIndicatorClip, options.clip ?? DEFAULTS.clip),
      duration: ds.tabIndicatorDuration ?? options.duration ?? DEFAULTS.duration,
      ease: ds.tabIndicatorEase ?? options.ease ?? DEFAULTS.ease,
    };

    if (cfg.duration !== null) container.style.setProperty('--tab-indicator-duration', parseFloat(cfg.duration) + 's');
    if (cfg.ease) container.style.setProperty('--tab-indicator-ease', cfg.ease);

    // ---- Elements ---------------------------------------------------------
    let createdBar = false;
    let indicator = container.querySelector('[data-tab-indicator-bar]');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.setAttribute('data-tab-indicator-bar', '');
      container.insertBefore(indicator, container.firstChild);
      createdBar = true;
    }
    indicator.classList.add('tab-indicator');
    indicator.setAttribute('aria-hidden', 'true');

    let tabs = Array.from(container.querySelectorAll('[data-tab-indicator-item]'));
    if (!tabs.length) tabs = Array.from(container.children).filter((el) => el !== indicator);
    if (!tabs.length) return null;
    tabs.forEach((tab) => tab.classList.add('tab-indicator-item'));

    const clipEls = [];
    if (cfg.clip) {
      tabs.forEach((tab) => {
        const source = tab.querySelector('[data-tab-indicator-label]') || tab.firstElementChild;
        const clip = document.createElement('div');
        clip.className = 'tab-indicator-clip';
        clip.setAttribute('aria-hidden', 'true');
        if (source) clip.appendChild(source.cloneNode(true));
        else clip.textContent = tab.textContent.trim();
        tab.appendChild(clip);
        clipEls.push(clip);
      });
    }

    // ---- State ------------------------------------------------------------
    function isActive(tab) {
      return (
        tab.classList.contains(cfg.activeClass) ||
        tab.classList.contains('w--current') ||
        tab.getAttribute('aria-selected') === 'true'
      );
    }

    function getActive() {
      return tabs.find(isActive) || tabs[0];
    }

    // If nothing is marked active, mark the first one (click mode only)
    if (cfg.click && !tabs.some(isActive)) tabs[0].classList.add(cfg.activeClass);

    let activeIndex = tabs.indexOf(getActive());
    let shownTab = tabs[activeIndex];

    // ---- Positioning ------------------------------------------------------
    function isVisible() {
      return container.getClientRects().length > 0 && container.offsetWidth > 0;
    }

    function measure(tab) {
      const c = container.getBoundingClientRect();
      const t = tab.getBoundingClientRect();
      return {
        x: t.left - c.left - container.clientLeft + container.scrollLeft,
        y: t.top - c.top - container.clientTop + container.scrollTop,
        w: t.width,
        h: t.height,
      };
    }

    function updateClips(target) {
      if (!clipEls.length) return;
      const targetIndex = tabs.indexOf(target);
      tabs.forEach((tab, i) => {
        const clip = clipEls[i];
        if (i === targetIndex) clip.style.clipPath = 'inset(0 0% 0 0%)';
        else if (i < targetIndex) clip.style.clipPath = 'inset(0 0% 0 100%)';
        else clip.style.clipPath = 'inset(0 100% 0 0%)';
      });
    }

    function moveTo(tab, instant = false) {
      if (!tab) return;
      shownTab = tab;
      if (!isVisible()) return; // hidden (e.g. inactive pane) — ResizeObserver catches it once visible

      const { x, y, w, h } = measure(tab);

      if (instant) {
        indicator.style.transition = 'none';
        clipEls.forEach((c) => (c.style.transition = 'none'));
      }

      indicator.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      indicator.style.width = w + 'px';
      indicator.style.height = h + 'px';
      updateClips(tab);

      if (instant) {
        void indicator.offsetWidth; // force reflow so the jump isn't animated
        indicator.style.transition = '';
        clipEls.forEach((c) => (c.style.transition = ''));
      }

      indicator.classList.add('is-ready');
    }

    function syncActive() {
      const active = getActive();
      const index = tabs.indexOf(active);
      moveTo(active);
      if (index !== activeIndex) {
        activeIndex = index;
        container.dispatchEvent(new CustomEvent('tabindicator:change', { detail: { index, tab: active } }));
      }
    }

    function setActive(index) {
      const tab = tabs[index];
      if (!tab) return;
      tabs.forEach((t) => t.classList.toggle(cfg.activeClass, t === tab));
      syncActive();
    }

    // ---- Events -----------------------------------------------------------
    const cleanups = [];
    function on(el, type, fn, opts) {
      el.addEventListener(type, fn, opts);
      cleanups.push(() => el.removeEventListener(type, fn, opts));
    }

    if (cfg.hover) {
      tabs.forEach((tab) => on(tab, 'mouseenter', () => moveTo(tab)));
      on(container, 'mouseleave', () => moveTo(getActive()));
    }

    // Keyboard users get the same feedback as hover
    tabs.forEach((tab) => on(tab, 'focus', () => tab.matches(':focus-visible') && moveTo(tab)));
    on(container, 'focusout', (e) => {
      if (!container.contains(e.relatedTarget)) moveTo(getActive());
    });

    if (cfg.click) {
      tabs.forEach((tab, i) =>
        on(tab, 'click', (e) => {
          if (tab.tagName === 'A' && (tab.getAttribute('href') || '#').startsWith('#')) e.preventDefault();
          setActive(i);
        })
      );
    }

    // Active state changed externally (Webflow tabs, other scripts)
    const mutationObserver = new MutationObserver(syncActive);
    tabs.forEach((tab) =>
      mutationObserver.observe(tab, { attributes: true, attributeFilter: ['class', 'aria-selected'] })
    );

    // ---- Responsive -------------------------------------------------------
    // ResizeObserver covers window resizes, breakpoint changes, font swaps,
    // text changes, and the group becoming visible after being hidden.
    // The indicator snaps (no animation) while resizing so it never lags behind.
    let rafId = null;
    function scheduleSnap() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => moveTo(shownTab, true));
    }

    let resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(scheduleSnap);
      resizeObserver.observe(container);
      tabs.forEach((tab) => resizeObserver.observe(tab));
    } else {
      on(window, 'resize', scheduleSnap);
    }
    on(window, 'orientationchange', scheduleSnap);
    on(window, 'load', scheduleSnap);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleSnap);

    // ---- Init -------------------------------------------------------------
    moveTo(shownTab, true);

    const api = {
      update: () => moveTo(getActive(), true),
      setActive,
      get activeIndex() {
        return activeIndex;
      },
      destroy() {
        cleanups.forEach((fn) => fn());
        mutationObserver.disconnect();
        resizeObserver && resizeObserver.disconnect();
        cancelAnimationFrame(rafId);
        clipEls.forEach((c) => c.remove());
        if (createdBar) indicator.remove();
        else indicator.classList.remove('tab-indicator', 'is-ready');
        tabs.forEach((tab) => tab.classList.remove('tab-indicator-item'));
        delete container._tabIndicator;
      },
    };

    container._tabIndicator = api;
    return api;
  }

  function initTabIndicators(root = document) {
    return Array.from(root.querySelectorAll('[data-tab-indicator]'))
      .map((el) => initTabIndicator(el))
      .filter(Boolean);
  }

  window.initTabIndicator = initTabIndicator;
  window.initTabIndicators = initTabIndicators;

  // Auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initTabIndicators());
  } else {
    initTabIndicators();
  }
})();
